import { pipe } from '@vaaas/fpts/function';
import { bind, filter, each, map } from '@vaaas/fpts/iter';
import { inside, pairs, unique } from '@vaaas/fpts/array';
import { duad } from '@vaaas/fpts/duad';
import { of as set, subset } from '@vaaas/fpts/set';
import { Tuple } from '@bloomberg/record-tuple-polyfill';
import { animate, display_axes, unhighlight_currently_highlighted_object } from './scene';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import * as THREE from 'three';
import App from './application-container';
import { uil_add_joint_smoothness, uil_make_edge_nonsmooth } from './uil_continuity';
import * as maths from './maths';
import { point_point_distance } from './vector3';
import { WrappedValue } from '@phi/wrapped-value';
import { edge_list_is_closed,
  mustNotDisplayEdge,
  find_all_pairs_in_listified_edge_list,
  find_all_pairs_in_unlistified_edge_list,
  vertices_of_face,
} from './lattice';
import { uid_unhighlight_all_axes } from './colours_materials_appearance';
import {
    find_edge_closest_vertex_to_coordinates,
    object_is_selected,
    push_nodups,
    request_setting_button_states,
    suppress_animate,
    suppress_undo_tracking,
    unsuppress_animate_if_ready,
    update_dom_text_values_and_coordinates,
} from './utility';
import HashTable from './hashtable';
import {
    uil_get_vertices_of_edge,
    uil_is_object_fixed,
    uil_find_edge_shared_vertex,
    uil_is_joint_smooth,
    uil_get_edges_of_face,
    uil_set_edge_fixing_of_tangent_at_vertex,
    uil_unfix_objects,
    uil_is_object_visible,
    uil_listify_edges,
    uil_get_point_at_edge_param,
    uil_get_tangent_at_edge_param,
} from './ui_logic';
import { withSpinner } from './state/spinner';
import { properly_split_invisible_face_connected_components,
    uid_deselect_all_objects,
    uid_select_object,
} from './ui_display';
import { FAINT_STRENGTH } from './vars_and_data_structures';

export function uid_smoothen_vertices(smooth_vertex_list, unsmooth_vertex_list) {
  var cannot_proceed_due_to_fixed_objects = false;
  unsmooth_vertex_list.forEach(function(vid){
    if (uil_is_object_fixed(vid))
      cannot_proceed_due_to_fixed_objects = true;
  });
  smooth_vertex_list.forEach(function(vid){
    if (uil_is_object_fixed(vid))
      cannot_proceed_due_to_fixed_objects = true;
  });

  if (cannot_proceed_due_to_fixed_objects) {
    display_message_to_user(LOG.WARN, "Cannot round a fixed vertex.");
    return;
  }

  suppress_animate();
  suppress_undo_tracking();

  // If all selected vertices are smooth, we must unsmoothen them first, and then we smoothen those.
  // Otherwise we smoothen only the unsmooth ones.
  var all_vertices;
  if (unsmooth_vertex_list.length > 0) {
    all_vertices = unsmooth_vertex_list;
  }
  else {
    all_vertices = smooth_vertex_list;
    uil_remove_vertex_smoothness(all_vertices);
  }

  // Calculate radius
  var min_length = 999999;
  all_vertices.forEach(function(vid){
    var es = uil_get_edges_of_vertex(vid);
    es.forEach(function(eid){
      var l = uil_get_edge_length(eid);
      if (l < min_length)
        min_length = l;
    });
  });

  // Set radius for smoothening vertex as 1/6 of shortest edge
  uil_smoothen_vertices(all_vertices, min_length/6.0);

  unsuppress_undo_tracking_if_ready(false);

  // Re-select the inital list of vertices
  smooth_vertex_list.forEach(function(vid){
    uid_select_object(vid);
  });
  unsmooth_vertex_list.forEach(function(vid){
    uid_select_object(vid);
  });

  unsuppress_animate_if_ready();
}

export function uid_smoothen_selected_edges() {
  suppress_animate();
  suppress_undo_tracking();

  if ( uid_selected_edges.length > 0 ) {
    var ids_of_edges_to_smoothen = new Array();
    uid_selected_edges.values().forEach(function(e) {
      ids_of_edges_to_smoothen.push(e.id);
    });

    // Before proceeding, break up all edge lists so that if there's two continuous fixed edges we'll break the chains.
    let edge_lists = split_lists_of_edges_to_smoothen_on_fixed_edges([ids_of_edges_to_smoothen]);
    edge_lists.forEach(function(edge_list){
      uil_smoothen_edges(edge_list);
    });
  }
  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
}

export function uid_smoothen_edge_lists_as_one(initial_listified_edge_lists, ignore_shift = false) {
  // Note: initial_edge_lists are expected to be already listified!

  // Before proceeding, break up all edge lists so that if there's two continuous fixed edges we'll break the chains.
  let edge_lists = split_lists_of_edges_to_smoothen_on_fixed_edges(initial_listified_edge_lists);

  var all_selected_edges_in_edge_lists = [];
  edge_lists.forEach(function(edge_list) {
    edge_list.forEach(function(eid){
      if (object_is_selected(eid))
        all_selected_edges_in_edge_lists.push(eid);
    });
  });

  suppress_animate();
  suppress_undo_tracking();

  var edge_lists_smoothened = 0;
  edge_lists.forEach(function(edge_list) {
    var res = uil_smoothen_chain_of_edges(edge_list, true);
    if (res)
      edge_lists_smoothened++;
    else
      display_message_to_user(LOG.WARN, "Failed to smoothen chain of edges. May include fixed edges or fixed tangencies in the middle, or may be violating 3-vertex limitation.");
  });

  // Reselect all edges that may have been deselected.
  all_selected_edges_in_edge_lists.forEach(function(eid) {
    uid_select_object(eid);
  });

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  set_button_states();
  return edge_lists_smoothened;
}

export function split_lists_of_edges_to_smoothen_on_fixed_edges(initial_edge_lists) {

  let edge_lists = [];
  initial_edge_lists.forEach(function(edge_list) {
    var edge_ids = [];
    var wv_is_open_list = new WrappedValue();
    var res = uil_listify_edges(edge_list, wv_is_open_list, edge_ids);
    // If the list is closed and has at least one fixed edge, rotate it until it starts at the last fixed edge
    if (!wv_is_open_list.v) {
      var fixed_index = 0;
      for (var i = 0; i < edge_ids.length; i++) {
        let eid = edge_ids[i];
        if (uil_is_object_fixed(eid))
          fixed_index = i;
      }
      var new_edge_ids = [];
      if (fixed_index > 0) {
        for (var i = 0; i < edge_ids.length; i++) {
          var j = i + fixed_index < edge_ids.length ? i + fixed_index : i + fixed_index - edge_ids.length;
          new_edge_ids[i] = edge_ids[j];
        }
        for (var i = 0; i < edge_ids.length; i++) {
          edge_ids[i] = new_edge_ids[i];
        }
      }
      // If there was at least one fixed edge, close the loop
      if (fixed_index > 0)
          edge_ids.push(edge_ids[0]);
    }

    var current_edge_list = [];
    edge_ids.forEach(function(eid){
      if (uil_is_object_fixed(eid)) {
        // If a fixed edge is encountered, use it as the last element of the current list and the first element of the next one
        current_edge_list.push(eid);
        let current_edge_list_copy = [... current_edge_list];
        edge_lists.push(current_edge_list_copy);
        current_edge_list = [eid];
      }
      else
        current_edge_list.push(eid);
    });
    edge_lists.push(current_edge_list);
  });

  // Get rid of any single-fixed-edge edge lists
  /*
  edge_lists.filter(edge_list =>
    !((edge_list.length == 1 && uil_is_object_fixed(edge_list[0])) ||
        (edge_list.length == 2 && uil_is_object_fixed(edge_list[0]) && uil_is_object_fixed(edge_list[1])))
  );
  */

  for (var i = edge_lists.length-1; i >= 0; i--) {
    var edge_list = edge_lists[i];
    if ((edge_list.length == 1 && uil_is_object_fixed(edge_list[0])) ||
        (edge_list.length == 2 && uil_is_object_fixed(edge_list[0]) && uil_is_object_fixed(edge_list[1])))
      edge_lists.splice(i,1);
  }

  return edge_lists;
}

export function uid_can_smoothen_chain_of_edges(edge_list) {
  // First make a copy and remove last edge in the copy if it's the same
  // as the first edge. This can happen when we break edge lists on fixed edges
  let edge_list_copy = [... edge_list];
  if (edge_list_copy[edge_list_copy.length-1] == edge_list_copy[0])
    edge_list_copy.splice(edge_list_copy.length-1, 1);

  var wv_is_open_list = new WrappedValue();
  var listified_edges = [];
  var res = uil_listify_edges(edge_list_copy, wv_is_open_list, listified_edges);
  if (!res) {
    return false;
  }

  if (listified_edges.length == 0)
    return false;
  else if (listified_edges.length == 1 && !uil_is_object_fixed(listified_edges[0]))
    return true;
  else if (listified_edges.length == 1 && uil_is_object_fixed(listified_edges[0]))
    return false;

  var total_fixed = 0;
  listified_edges.forEach(function(eid){
    if (uil_is_object_fixed(eid))
      total_fixed++;
  });

  if (wv_is_open_list.v) {
    if (total_fixed > 2 ||
        (total_fixed == 2 && (!uil_is_object_fixed(listified_edges[0]) || !uil_is_object_fixed(listified_edges[listified_edges.length-1])) ) ||
        (total_fixed == 1 && !uil_is_object_fixed(listified_edges[0]) && !uil_is_object_fixed(listified_edges[listified_edges.length-1]) )
      )
      display_message_to_user(LOG.WARN, "You cannot smoothen chains with fixed edges in the middle of the chain.");

    if (total_fixed > 2)
      return false;
    var tmp_es = [... listified_edges];
    if (uil_is_object_fixed(tmp_es[0]))
      tmp_es.shift();
    if (uil_is_object_fixed(tmp_es[tmp_es.length-1]))
      tmp_es.pop();
    if (tmp_es.length > 1)
      return (uil_can_smoothen_edges_as_one(tmp_es));
    else
      return true;
  }
  else {
    if (total_fixed > 1) {
      display_message_to_user(LOG.WARN, "You cannot smoothen loops with more than one fixed edge.");
      return false;
    }
    if (total_fixed == 1) {
      var fixed_index = 0;
      for (var i = 0; i < listified_edges.length; i++) {
        if (uil_is_object_fixed(listified_edges[i]))
        fixed_index = i;
      }
      for (var i = 0; i <= fixed_index-1; i++)
      listified_edges.push(listified_edges.shift());
      var tmp_es = [... listified_edges];
      tmp_es.shift();
      return (uil_can_smoothen_edges_as_one(tmp_es));
    }
    else {
      return (uil_can_smoothen_edges_as_one(listified_edges));
    }
  }
}

export function uid_unsmoothen_edge_lists(edge_lists) {

  suppress_animate();
  suppress_undo_tracking();

  edge_lists.forEach(function(edge_list){
    if ( edge_list.length > 1 )
    // Remove tangency between edges in this list
    for (var i = 0; i < edge_list.length - 1; i++) {
        uil_remove_joint_smoothness([[edge_list[i], edge_list[i+1]]], false, true);
    }
  });
  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
}

/** This is the main smoothening function. Depending on what objects are selected it will call different smoothing functions. */
export const smoothen_objects = (() => {
    const only_faces_selected = () =>
        uid_selected_edges.length === 0
        && uid_selected_vertices.length === 0
        && uid_selected_faces.length > 0

    const only_vertices_selected = () =>
        uid_selected_edges.length === 0
        && uid_selected_vertices.length > 0
        && uid_selected_faces.length === 0

    const all_vertices_are_on_edges = () => pipe(
        uid_selected_edges.keys(),
        bind(uil_get_vertices_of_edge),
        set,
        subset(set(uid_selected_vertices.keys())),
    );

    const total_selected_objects = () => uid_selected_vertices.length + uid_selected_edges.length + uid_selected_faces.length;

    const gonna_be_slow = () => total_selected_objects() >= 10;

    function smoothen_objects(fix_vertices) {
        if (only_faces_selected())
            // If only faces are selected and they are adjacent, it will smoothen them.
            smoothen_faces(uid_selected_faces.keys(), fix_vertices);
        else if (uid_selected_edges.length > 0 && uid_selected_faces.length == 0) {
            // If no faces are selected, and any vertices selected lie on selected edges, smoothen all edge lists.
            if (all_vertices_are_on_edges())
                smoothen_edges(uid_selected_edges.keys(), fix_vertices);
            else
                smoothen_area(uid_selected_vertices.keys(), uid_selected_edges.keys(), uid_selected_faces.keys(), fix_vertices);
        } else if (only_vertices_selected())
            // Smoothen all vertices
            smoothen_vertices(uid_selected_vertices.keys());
        else
            // Otherwise smoothen the entire area
            smoothen_area(uid_selected_vertices.keys(), uid_selected_edges.keys(), uid_selected_faces.keys(), fix_vertices);

        App.state.selected_vertices_count = -Infinity; // hack that forces refresh of smoothen button, find cleaner solution later
        request_setting_button_states();
    }

    return withSpinner('Smoothening', smoothen_objects, gonna_be_slow)
})();

export function smoothen_faces(face_ids, fix_vertices) {
  //Identify all pairs of selected faces that share an edge
  var common_edge_ids = [];
  for (var i = 0; i < face_ids.length - 1; i++) {
    for (var j = i + 1; j < face_ids.length; j++) {
      var f1 = face_ids[i];
      var f2 = face_ids[j];
      var es_f1 = uil_get_edges_of_face(f1);
      var es_f2 = uil_get_edges_of_face(f2);
      var common_edge_id = -1;
      es_f1.forEach(function(e1id){
        if (es_f2.indexOf(e1id) != -1)
          if (common_edge_id == -1)
            // Found a common edge
            common_edge_id = e1id;
          else
            // Found another common edge - must abort
            common_edge_id = -1;
      });
      if (common_edge_id != -1) {
        common_edge_ids.push(common_edge_id);
      }
    }
  }
  // Make sure we are smoothing a "nice chain" of faces, otherwise do an area smoothen. To check this we see whether any face has more than
  // one or two common_edges on it. And also that no common edges share any vertex (i.e. the number of vertices on all the common edges
  // is twice the number of common edges).
  var all_faces_have_one_or_two_common_edges = true;
  face_ids.forEach(function(fid){
    var es = uil_get_edges_of_face(fid);
    const filteredArray = es.filter(value => common_edge_ids.includes(value));
    if (filteredArray.length < 1 || filteredArray.length > 2)
      all_faces_have_one_or_two_common_edges = false;
  });
  var common_edge_id_vertices = [];
  common_edge_ids.forEach(function(eid){
    push_nodups(uil_get_vertices_of_edge(eid)[0], common_edge_id_vertices);
    push_nodups(uil_get_vertices_of_edge(eid)[1], common_edge_id_vertices);
  });
  if (!all_faces_have_one_or_two_common_edges || common_edge_id_vertices.length != common_edge_ids.length * 2) {
    smoothen_area(uid_selected_vertices.keys(), uid_selected_edges.keys(), uid_selected_faces.keys(), fix_vertices);
    return;
  }

  suppress_animate()
  suppress_undo_tracking();

  var vertices_that_we_fixed = [];
  if (fix_vertices) {
    common_edge_ids.forEach(function(eid){
      if (!uil_is_object_fixed(uil_get_vertices_of_edge(eid)[0]))
        push_nodups(uil_get_vertices_of_edge(eid)[0], vertices_that_we_fixed);
      if (!uil_is_object_fixed(uil_get_vertices_of_edge(eid)[1]))
        push_nodups(uil_get_vertices_of_edge(eid)[1], vertices_that_we_fixed);
    });
  }
  if (vertices_that_we_fixed.length > 0)
    uil_fix_objects(vertices_that_we_fixed);

  common_edge_ids.forEach(function(common_edge_id){
    uil_make_edge_smooth(common_edge_id);
  });

  if (vertices_that_we_fixed.length > 0)
    uil_unfix_objects(vertices_that_we_fixed);

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  if (common_edge_ids.length == 1)
    display_message_to_user(LOG.INFO, "Smoothened " + common_edge_ids.length + " adjacent face pair.");
  else if (common_edge_ids.length > 1)
    display_message_to_user(LOG.INFO, "Smoothened " + common_edge_ids.length + " adjacent face pairs.");
}

export function smoothen_edges(edge_ids, fix_vertices) {

  var initial_edge_lists = get_listified_lists_of_connected_edges_in_edge_list(edge_ids, true);

  var initial_plain_edge_lists = [];
  initial_edge_lists.forEach(function(edge_list_obj){
    initial_plain_edge_lists.push(edge_list_obj.edges);
  });

  // Before proceeding, break up all edge lists so that if there's two continuous fixed edges we'll break the chains.
  let edge_lists = split_lists_of_edges_to_smoothen_on_fixed_edges(initial_plain_edge_lists);

  if (edge_lists.length == 0) {
    display_message_to_user(LOG.WARN, "Unable to smoothen the selected edges. Perhaps they do not form independent chains. Please change your selection and try again.");
  }

  suppress_animate();
  suppress_undo_tracking();

    // Before we start, check if there's any boundary edges, in which case we properly split invisible parts of the model
    let boundary_edges_found = false;
    let boundary_edge_vertices = [];
    edge_lists.forEach(function(edge_list){
      edge_list.forEach(function(eid){
        if (!uil_is_object_visible(uil_get_faces_of_edge(eid)[0]) || !uil_is_object_visible(uil_get_faces_of_edge(eid)[1])) {
          boundary_edges_found = true;
          push_nodups(uil_get_vertices_of_edge(eid)[0], boundary_edge_vertices);
          push_nodups(uil_get_vertices_of_edge(eid)[1], boundary_edge_vertices);
        }
      });
    });
    if (boundary_edges_found)
      properly_split_invisible_face_connected_components(boundary_edge_vertices, false, false);

  // Before proceeding with the smoothing, check whether any of the edge pairs in any of the edge lists is not tangent.
  // In that case, we add the tangency
  // Also check whether each independent edge list can be smoothened at all, if not skip it.

  // Also remove any internal fixed edge tangents
  let unfixed_tangents = 0;

  var non_tangent_edge_pairs = [];
  edge_lists.forEach(function(edge_list){

    /*
    // Remove fixed tangents
    edge_list.forEach(function(eid){
      uil_get_vertices_of_edge(eid).forEach(function(vid){
        var wv_is_tan = new WrappedValue();
        var res = uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv_is_tan);
        if (wv_is_tan.v) {
          unfix_edge_tangent_at_vertex(eid, [vid]);
          unfixed_tangents++;
        }
      });
    });
    */

    var edge_pairs = find_all_pairs_in_unlistified_edge_list(edge_list);
    edge_pairs.forEach(function(edge_pair){
      if (!uil_is_joint_smooth(edge_pair[0], edge_pair[1]) &&
          !(uil_is_object_fixed(edge_pair[0] && uil_is_object_fixed(edge_pair[1])))) {
        let found_pair_in_list = false;
        non_tangent_edge_pairs.forEach(function(ntep){
          if ((ntep[0] == edge_pair[0] && ntep[1] == edge_pair[1]) ||
                (ntep[0] == edge_pair[1] && ntep[1] == edge_pair[0]))
            found_pair_in_list = true;
        });
        if (!found_pair_in_list)
          non_tangent_edge_pairs.push(edge_pair);
      }
    });
  });

  if (non_tangent_edge_pairs.length > 0) {
    make_edges_tangent(non_tangent_edge_pairs, fix_vertices);
  }

  /*
  if (unfixed_tangents == 1)
    display_message_to_user(LOG.INFO, "One edge tangent was unlocked to allow smoothing edge chain");
  else if (unfixed_tangents > 1)
    display_message_to_user(LOG.INFO, unfixed_tangents + " edge tangents were unlocked to allow smoothing edge chain");
  */

  var vertices_that_we_fixed = [];
  if (fix_vertices) {
    edge_lists.forEach(function(edge_list){
      edge_list.forEach(function(eid){
        if (!uil_is_object_fixed(uil_get_vertices_of_edge(eid)[0]))
          push_nodups(uil_get_vertices_of_edge(eid)[0], vertices_that_we_fixed);
        if (!uil_is_object_fixed(uil_get_vertices_of_edge(eid)[1]))
          push_nodups(uil_get_vertices_of_edge(eid)[1], vertices_that_we_fixed);
      });
    });
  }

  if (vertices_that_we_fixed.length > 0)
    uil_fix_objects(vertices_that_we_fixed);

  var res = uid_smoothen_edge_lists_as_one(edge_lists, true);
  if (vertices_that_we_fixed.length > 0)
    uil_unfix_objects(vertices_that_we_fixed);
  if (res)
    display_message_to_user(LOG.INFO, "Smoothened " + edge_lists.length + " edge chains.");

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  request_setting_button_states();
}

export function smoothen_vertices(vertex_ids) {
  // Calculate radius
  var min_length = 999999;
  vertex_ids.forEach(function(vid){
    var es = uil_get_edges_of_vertex(vid);
    es.forEach(function(eid){
      var l = uil_get_edge_length(eid);
      if (l < min_length)
        min_length = l;
    });
  });
  var res = uil_smoothen_vertices(vertex_ids, min_length/6.0);
  if (res)
    display_message_to_user(LOG.INFO, "Smoothened " + vertex_ids.length + " vertices.");
}

export function smoothen_area(vertex_ids, edge_ids, face_ids, fix_vertices) {
  var vs = [];
  var direct_vs = [];
  var es = [];
  var fs = [];

  var only_edges_selected;

  // All selected edges obviously go into es
  edge_ids.forEach(function(eid){
    push_nodups(eid, es);
  });

  // All selected vertices go into vs, unless they only lie on selected edges, in which case
  // they are discarded since the selected edges are more important, and we don't want the vertices
  // to "contaminate" the edge lists. Only vertices that lie on none of the selected edges will be
  // added to vs.
  vertex_ids.forEach(function(vid){
    var ves = uil_get_edges_of_vertex(vid);
    var lies_on_selected_edge = false;
    ves.forEach(function(eid) {
      if (es.indexOf(eid) != -1)
        lies_on_selected_edge = true;
    });
    if (!lies_on_selected_edge) {
      // Add the vertex to vs
      push_nodups(vid, vs);
      push_nodups(vid, direct_vs);
    }
  });

  // All faces are added to fs, and they also contribute their edges and vertices.
  face_ids.forEach(function(fid){
    push_nodups(fid, fs);
    var fvs = vertices_of_face(fid);
    fvs.forEach(function(vid){
      push_nodups(vid, vs);
    });
    var fes = uil_get_edges_of_face(fid);
    fes.forEach(function(eid){
      push_nodups(eid, es);
    });
  });

  var edge_pairs_to_smoothen = [];
  var edge_pairs_smoothened = 0;
  var vertices_left_out = 0;
  var edge_pairs_left_out = 0;
  var edge_pairs_already_smooth = 0;

  if (es.length > 0 && fs.length == 0 && vs.length == 0)
    only_edges_selected = true;

  suppress_animate();
  suppress_undo_tracking();

  // If any edge lists are selected, but no faces, basically call uid_smoothen_edge_lists_as_one on them.
  // We can have vertices selected as we'll deal with them afterwards.
  if (es.length > 0 && fs.length == 0) {
    var edge_lists = get_listified_lists_of_connected_edges_in_edge_list(es, true);

    var vertices_that_we_fixed = [];
    if (fix_vertices) {
      es.forEach(function(eid){
        if (!uil_is_object_fixed(uil_get_vertices_of_edge(eid)[0]))
          push_nodups(uil_get_vertices_of_edge(eid)[0], vertices_that_we_fixed);
        if (!uil_is_object_fixed(uil_get_vertices_of_edge(eid)[1]))
          push_nodups(uil_get_vertices_of_edge(eid)[1], vertices_that_we_fixed);
      });
    }
    uil_fix_objects(vertices_that_we_fixed);

    var plain_edge_lists = [];
    edge_lists.forEach(function(edge_list_obj){
      plain_edge_lists.push(edge_list_obj.edges);
    });

    uid_smoothen_edge_lists_as_one(plain_edge_lists, true);
    uil_unfix_objects(vertices_that_we_fixed);
  }

  suppress_ui_messages();

  // Take all vertices, identify all edge pairs to smoothen then add them to the list
  vs.forEach(function(vid) {
    var ves = uil_get_edges_of_vertex(vid);
    var ves_common_vertex_tangents = [];
    for (var i = 0; i < ves.length; i++) {
      var p0 = uil_get_point_at_edge_param(ves[i], 0);
      var p1 = uil_get_point_at_edge_param(ves[i], 1);
      var t;
      if (point_point_distance(p0, uid_vertices.getItem(vid).object3d.position) <
          point_point_distance(p1, uid_vertices.getItem(vid).object3d.position))
        t = uil_get_tangent_at_edge_param(ves[i], 0);
      else
        t = uil_get_tangent_at_edge_param(ves[i], 1);
        ves_common_vertex_tangents[i] = t;
    }

    if (ves.length == 4) {
      if (uil_is_object_visible(ves[0]) && uil_is_object_visible(ves[2]) &&
          (ves_common_vertex_tangents[0].angleTo(ves_common_vertex_tangents[2]) < 45.0/180.0*Math.PI ||
          ves_common_vertex_tangents[0].angleTo(ves_common_vertex_tangents[2]) > 135.0/180.0*Math.PI))
        if (direct_vs.indexOf(vid) != -1 || ( es.indexOf(ves[0]) != -1 && es.indexOf(ves[2]) != -1 )) {
          // Add the edge pair if it is on a vertex directly selected, OR if both of its edges have been
          // selected either directly or via selected faces.
          edge_pairs_to_smoothen.push([ves[0], ves[2]]);
        }
      else
        edge_pairs_left_out++;
      if (uil_is_object_visible(ves[1]) && uil_is_object_visible(ves[3]) &&
          (ves_common_vertex_tangents[1].angleTo(ves_common_vertex_tangents[3]) < 45.0/180.0*Math.PI ||
          ves_common_vertex_tangents[1].angleTo(ves_common_vertex_tangents[3]) > 135.0/180.0*Math.PI))
        if (direct_vs.indexOf(vid) != -1 || ( es.indexOf(ves[1]) != -1 && es.indexOf(ves[3]) != -1 )) {
          // Add the edge pair if it is on a vertex directly selected, OR if both of its edges have been
          // selected either directly or via selected faces.
          edge_pairs_to_smoothen.push([ves[1], ves[3]]);
        }
      else
        edge_pairs_left_out++;
    }
    else if (ves.length < 4) {
      var mini1 = 0;
      var minj1 = 0;
      var mina1 = 99999;
      var mini2 = 0;
      var minj2 = 0;
      var mina2 = 99999;
      for (var i = 0; i < ves.length; i++ ) {
        for (var j = i+1; j < ves.length; j++) {
          var a = Math.min(Math.abs(ves_common_vertex_tangents[i].angleTo(ves_common_vertex_tangents[j]) * 180/Math.PI),
                          Math.abs((ves_common_vertex_tangents[i].angleTo(ves_common_vertex_tangents[j]) - Math.PI) * 180/Math.PI));
          if (a < mina1) {
            mina2 = mina1;
            mini2 = mini1;
            minj2 = minj1;
            mina1 = a;
            mini1 = i;
            minj1 = j;
          }
          //else if (a < mina2) {
          //  mina2 = mina1;
          //  mini2 = mini1;
          //  minj2 = minj1;
          //  mina1 = a;
          //  mini1 = i;
          //  minj1 = j;
          //}
        }
      }
      if (mina1 < 45) {
        if ( direct_vs.indexOf(vid) != -1 || (es.indexOf(ves[mini1]) != -1 && es.indexOf(ves[minj1]) != -1 )) {
          // Add the edge pair if it is on a vertex directly selected, OR if both of its edges have been
          // selected either directly or via selected faces.
          edge_pairs_to_smoothen.push([ves[mini1], ves[minj1]]);
        }
      }
    }
    else {
      vertices_left_out++;
    }
  });

  // If necessary, fix vertices
  var vertices_that_we_fixed = [];
  if (fix_vertices) {
    vs.forEach(function(vid) {
      if (!uil_is_object_fixed(vid)) {
        vertices_that_we_fixed.push(vid);
      }
    });
    uil_fix_objects(vertices_that_we_fixed);
  }

  // Smoothen all edge pairs
  edge_pairs_to_smoothen.forEach(function(edge_pair) {
    if (uil_is_joint_smooth(edge_pair[0], edge_pair[1]) == 0)
      edge_pairs_smoothened++;
    else if (uil_is_joint_smooth(edge_pair[0], edge_pair[1]) == 1)
      edge_pairs_already_smooth++;
  });
  uid_smoothen_edge_lists_as_one(edge_pairs_to_smoothen, true);

  // Smoothen T-junctions
  // Identify T-junctions based on the vertices: Vertices with 3 edges, where 2 are tangent.
  vs.forEach(function(vid){
    var ves = uil_get_edges_of_vertex(vid);
    var teid = 0;
    var tfid = 0;
    var tfsa = [];
    var tfsb = [];
    if (ves.length == 3) {
      if (uil_is_joint_smooth(ves[0], ves[1]) == 1) {
        teid = ves[2];
        var tfsa = uil_get_faces_of_edge(ves[0]);
        var tfsb = uil_get_faces_of_edge(ves[1]);
      }
      else if (uil_is_joint_smooth(ves[1], ves[2]) == 1) {
        teid = ves[2];
        var tfsa = uil_get_faces_of_edge(ves[1]);
        var tfsb = uil_get_faces_of_edge(ves[2]);
      }
      else if (uil_is_joint_smooth(ves[0], ves[2]) == 1) {
        teid = ves[1];
        var tfsa = uil_get_faces_of_edge(ves[0]);
        var tfsb = uil_get_faces_of_edge(ves[2]);
      }
      var tfs = tfsa.filter(value => tfsb.includes(value));
      if (tfs.length == 1 && uil_is_object_visible(tfs[0]))
        tfid = tfs[0];
    }
    if (teid > 0 && tfid > 0)
      uil_make_edge_tangent_to_face(teid, tfid);
  });

  // Unfix vertices
  uil_unfix_objects(vertices_that_we_fixed);

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  unsuppress_ui_messages_if_ready();


  var str0 = "Smoothened area. ";
  var str1 = "";
  var str2 = "";
  var str3 = "";
  var str4 = "";

  if (edge_pairs_smoothened == 0)
    str1 = "No edge pairs were smoothened. ";
  else if (edge_pairs_smoothened == 1)
    str1 = "One edge pair was smoothened. ";
  else
    str1 = edge_pairs_smoothened + " edge pairs were smoothened. ";
  if (edge_pairs_already_smooth == 0)
    str2 = "";
  else if (edge_pairs_already_smooth == 1)
    str2 = "One edge pair was already smooth. ";
  else
    str2 = edge_pairs_already_smooth + " edge pairs were already smooth. ";
  if (edge_pairs_left_out == 0)
    str3 = "";
  else if (edge_pairs_left_out == 1)
    str3 = "One edge pair was left out. ";
  else
    str3 = edge_pairs_left_out + " edge pairs were left out. ";
  if (vertices_left_out == 0)
    str4 = "";
  else if (vertices_left_out == 1)
    str4 = "One vertex was left out. ";
  else
    str4 = vertices_left_out + " vertices were left out. ";

  display_message_to_user(LOG.INFO, str0 + str1 + str2 + str3 + str4);
}

export const unsmoothen_objects = (() => {
    const only_vertices_selected = () => uid_selected_vertices.length > 0 && uid_selected_edges.length == 0 && uid_selected_faces.length == 0;
    const only_two_faces_selected = () => uid_selected_vertices.length == 0 && uid_selected_edges.length == 0 && uid_selected_faces.length >= 2;
    const total_selected_objects = () => uid_selected_vertices.length + uid_selected_edges.length + uid_selected_faces.length;
    const gonna_be_slow = () => total_selected_objects() >= 5;

    function unsmoothen_vertices() {
        // Check whether there is at least one smooth vertex
        const vertices_to_unsmoothen = uid_selected_vertices.keys().filter(uil_is_vertex_smooth);
        if (vertices_to_unsmoothen.length > 0)
            uil_remove_vertex_smoothness(vertices_to_unsmoothen);
    }

    function unsmoothen_edge_pairs() {
        // Check whether there are pairs of smooth faces
        const edges_to_smoothen = [];
        for (let i = 0; i < uid_selected_faces.length - 1; i++) {
            for (let j = i+1; j < uid_selected_faces.length; j++) {
                const f1 = uid_selected_faces.getKeyAtIndex(i);
                const f2 = uid_selected_faces.getKeyAtIndex(j);
                const es_f1 = uil_get_edges_of_face(f1);
                const es_f2 = uil_get_edges_of_face(f2);

                const common_edges = es_f1.filter(inside(es_f2));
                if (common_edges.length !== 1) continue;
                const common_edge_id = common_edges[0];

                const vs = uil_get_vertices_of_edge(common_edge_id);
                const es_v1 = uil_get_edges_of_vertex(vs[0]);
                const es_v2 = uil_get_edges_of_vertex(vs[1]);

                let ef1v1, ef1v2, ef2v1, ef2v2;
                for (const ev1id of es_v1) {
                    if (ev1id != common_edge_id && es_f1.includes(ev1id))
                        ef1v1 = ev1id;
                    if (ev1id != common_edge_id && es_f2.includes(ev1id))
                        ef2v1 = ev1id;
                }
                for (const ev2id of es_v2) {
                    if (ev2id != common_edge_id && es_f1.includes(ev2id))
                        ef1v2 = ev2id;
                    if (ev2id != common_edge_id && es_f2.includes(ev2id))
                        ef2v2 = ev2id;
                }
                if (uil_is_joint_smooth(ef1v1, ef2v1) == 1 || uil_is_joint_smooth(ef1v2, ef2v2) == 1)
                    edges_to_smoothen.push(common_edge_id);
            }
        }
        for (const eid of set(edges_to_smoothen))
            uil_make_edge_nonsmooth(eid);
    }

    function unsmoothen_rest() {
        // Collect all contributed edges and see what we can do to smoothen them.
        const all_edges = unique([
            ...uid_selected_edges.keys(),
            ...uid_selected_faces.keys().flatMap(fid => uil_get_edges_of_face(fid)),
        ]);

        const edge_lists = get_listified_lists_of_connected_edges_in_edge_list(uid_selected_edges.keys(), false);
        const plain_edge_lists = edge_lists.map(x => x.edges);
        make_edges_not_tangent(plain_edge_lists);

        // Check whether ALL joints are already unsmooth
        let all_joints_unsmooth = true;
        for (const edge_list_obj of edge_lists) {
            const edge_list = edge_list_obj.edges;
            if (!edge_list_obj.is_open)
                edge_list.push(edge_list[0]); //Add first element at end to close loop.
            for (let i = 0; i < edge_list.length - 1; i++)
                if (uil_is_joint_smooth(edge_list[i], edge_list[i+1]))
                    all_joints_unsmooth = false;
        }
    }

    function unsmoothen_objects() {
        if (only_vertices_selected())
            unsmoothen_vertices();
        else if (only_two_faces_selected())
            unsmoothen_edge_pairs();
        else
            unsmoothen_rest();
    }

    return withSpinner('Unsmoothening', unsmoothen_objects, gonna_be_slow);
})();

export function find_type_of_smoothening_for_objects(vertices, edges, faces) {
  if (uid_raynode != uid_objects_raynode || (uid_selected_vertices.length == 0 && uid_selected_edges.length == 0 && uid_selected_faces.length == 0)) {
      return "none";
  }

  var all_fixed = true;
  edges.forEach(function(eid){
    if (!uil_is_object_fixed(eid))
    all_fixed = false;
  });
  faces.forEach(function(fid){
    if (!uil_is_object_fixed(fid))
    all_fixed = false;
  });
  vertices.forEach(function(vid){
    if (!uil_is_object_fixed(vid))
    all_fixed = false;
  });

  if (all_fixed) {
      return "none";
  }

  // Check whether we have any smooth objects that we can unsmooth
  if (uid_selected_vertices.length > 0 && uid_selected_edges.length == 0 && uid_selected_faces.length == 0) {
    return "vertices";
  }
  else if (uid_selected_edges.length > 0 && uid_selected_faces.length == 0) {
    // Edges and/or vertices selected. Check if all vertices are on the edges, otherwise handle it as area
    var all_edge_vertices = [];
    var all_vertices_are_on_edges = true;
    edges.forEach(function(eid){
      push_nodups(uil_get_vertices_of_edge(eid)[0], all_edge_vertices);
      push_nodups(uil_get_vertices_of_edge(eid)[1], all_edge_vertices);
    });
    vertices.forEach(function(vid){
      if (all_edge_vertices.indexOf(vid) == -1)
        all_vertices_are_on_edges = false;
    });

    if (!all_vertices_are_on_edges) {
        // Cannot handle this as edge list, revert to area.
    }
    else {
      // We're examining smoothening edge chains. Check if all pairs of edges are already smooth or not.
      var edge_lists = get_listified_lists_of_connected_edges_in_edge_list(edges, false);
      if (edge_lists.length == 0) {
        // Cannot handle this as edge list, revert to area.
      }
      else {
        return "edges";
      }
    }
  }
  else if (uid_selected_vertices.length == 0 && uid_selected_edges.length == 0 && uid_selected_faces.length >= 2) {
    // Check whether there are proper pairs of smooth faces

    var common_edges = [];
    for (var i = 0; i < uid_selected_faces.length - 1; i ++) {
      for (var j = i+1; j < uid_selected_faces.length; j ++) {
        var f1 = uid_selected_faces.getKeyAtIndex(i);
        var f2 = uid_selected_faces.getKeyAtIndex(j);
        var es_f1 = uil_get_edges_of_face(f1);
        var es_f2 = uil_get_edges_of_face(f2);
        var common_edge_id = -1;
        es_f1.forEach(function(e1id){
          if (es_f2.indexOf(e1id) != -1)
            if (common_edge_id == -1)
              // Found a common edge
              common_edge_id = e1id;
            else
              // Found another common edge - must abort
              common_edge_id = -1;
        });

        if (common_edge_id != -1)
          push_nodups(common_edge_id, common_edges);
      }
    }

    // To make sure we're able to smoothen faces, make sure see whether any face has more than
    // one or two common_edges on it. And also that no common edges share any vertex (i.e. the number of vertices on all the common edges
    // is twice the number of common edges).
    var all_faces_have_one_or_two_common_edges = true;
    faces.forEach(function(fid){
      var es = uil_get_edges_of_face(fid);
      const filteredArray = es.filter(value => common_edges.includes(value));
      if (filteredArray.length < 1 || filteredArray.length > 2)
        all_faces_have_one_or_two_common_edges = false;
    });
    var common_edge_id_vertices = [];
    common_edges.forEach(function(eid){
      push_nodups(uil_get_vertices_of_edge(eid)[0], common_edge_id_vertices);
      push_nodups(uil_get_vertices_of_edge(eid)[1], common_edge_id_vertices);
    });
    if (!all_faces_have_one_or_two_common_edges || common_edge_id_vertices.length != common_edges.length * 2) {
      // Cannot handle it as faces, revert to area
    }
    else {
      return "faces";
    }
  }

  // If we reached here it means that we're examining smoothing an area.
  return "area";
}

export function set_smoothen_unsmoothen_selected_objects_state(wv_smoothen_state, wv_unsmoothen_state) {

  var smoothen_type = find_type_of_smoothening_for_objects(uid_selected_vertices.keys(), uid_selected_edges.keys(), uid_selected_faces.keys());

  if (smoothen_type == "none") {
    wv_smoothen_state.v = "disabled";
    wv_unsmoothen_state.v = "disabled";
    return;
  }
  else if (smoothen_type == "vertices") {
    var all_vertices_smooth = true;
    var all_vertices_nonsmooth = true;
    uid_selected_vertices.keys().forEach(function(vid){
      if (uil_is_vertex_smooth(vid))
        all_vertices_nonsmooth = false;
      else
        all_vertices_smooth = false;
    });
    if (all_vertices_smooth) {
      wv_smoothen_state.v = "selected";
      wv_unsmoothen_state.v = "enabled";
    }
    else if (all_vertices_nonsmooth) {
      wv_smoothen_state.v = "enabled";
      wv_unsmoothen_state.v = "disabled";
    }
    else {
      wv_smoothen_state.v = "enabled";
      wv_unsmoothen_state.v = "enabled";
    }
    return;
  }
  else if (smoothen_type == "edges") {
    // We're examining smoothening edge chains. Check if all pairs of edges are already smooth or not.
    var edge_lists = get_listified_lists_of_connected_edges_in_edge_list(uid_selected_edges.keys(), false);
    // Check whether ALL joints are already unsmooth
    var all_joints_smooth = true;
    var all_joints_nonsmooth = true;
    edge_lists.forEach(function(edge_list_obj) {
      var edge_list = edge_list_obj.edges;
      if (!edge_list_obj.is_open)
        edge_list.push(edge_list[0]); // Append first element at end to close loop
      for (var i = 0; i < edge_list.length-1; i++) {
        if (uil_is_joint_smooth(edge_list[i], edge_list[i+1]))
          all_joints_nonsmooth = false;
        else
          all_joints_smooth = false;
      }
    });
    if (all_joints_smooth) {
      wv_smoothen_state.v = "selected";
      wv_unsmoothen_state.v = "enabled";
    }
    else if (all_joints_nonsmooth) {
      wv_smoothen_state.v = "enabled";
      wv_unsmoothen_state.v = "disabled";
    }
    else {
      wv_smoothen_state.v = "enabled";
      wv_unsmoothen_state.v = "enabled";
    }
    return;
  }
  else if (smoothen_type == "faces") {
    var common_edges = [];
    for (var i = 0; i < uid_selected_faces.length - 1; i ++) {
      for (var j = i+1; j < uid_selected_faces.length; j ++) {
        var f1 = uid_selected_faces.getKeyAtIndex(i);
        var f2 = uid_selected_faces.getKeyAtIndex(j);
        var es_f1 = uil_get_edges_of_face(f1);
        var es_f2 = uil_get_edges_of_face(f2);
        var common_edge_id = -1;
        es_f1.forEach(function(e1id){
          if (es_f2.indexOf(e1id) != -1)
            if (common_edge_id == -1)
              // Found a common edge
              common_edge_id = e1id;
            else
              // Found another common edge - must abort
              common_edge_id = -1;
        });

        if (common_edge_id != -1)
          push_nodups(common_edge_id, common_edges);
      }
    }

    // Check if the edges joining faces are smooth/non-smooth
    var all_edge_pairs_smooth = true;
    var all_edge_pairs_nonsmooth = true;

    common_edges.forEach(function(eid){
      var vs = uil_get_vertices_of_edge(eid);
      var es_v1 = uil_get_edges_of_vertex(vs[0]);
      var es_v2 = uil_get_edges_of_vertex(vs[1]);

      var f1 = uil_get_faces_of_edge(eid)[0];
      var f2 = uil_get_faces_of_edge(eid)[1];
      var es_f1 = uil_get_edges_of_face(f1);
      var es_f2 = uil_get_edges_of_face(f2);
      var ef1v1, ef1v2, ef2v1, ef2v2;
      es_v1.forEach(function(ev1id) {
        if (ev1id != eid && es_f1.indexOf(ev1id) != -1)
          ef1v1 = ev1id;
        if (ev1id != eid && es_f2.indexOf(ev1id) != -1)
          ef2v1 = ev1id;
      });
      es_v2.forEach(function(ev2id) {
        if (ev2id != eid && es_f1.indexOf(ev2id) != -1)
          ef1v2 = ev2id;
        if (ev2id != eid && es_f2.indexOf(ev2id) != -1)
          ef2v2 = ev2id;
      });
      if (uil_is_joint_smooth(ef1v1,ef2v1))
        all_edge_pairs_nonsmooth = false;
      else
        all_edge_pairs_smooth = false;
      if (uil_is_joint_smooth(ef1v2,ef2v2))
        all_edge_pairs_nonsmooth = false;
      else
        all_edge_pairs_smooth = false;
    });

    if (all_edge_pairs_smooth) {
      wv_smoothen_state.v = "selected";
      wv_unsmoothen_state.v = "enabled";
    }
    else if (all_edge_pairs_nonsmooth) {
      wv_smoothen_state.v = "enabled";
      wv_unsmoothen_state.v = "disabled";
    }
    else {
      wv_smoothen_state.v = "enabled";
      wv_unsmoothen_state.v = "enabled";
    }
    return;
  }
  else {
    // It's area. You can always smoothen more the area. You can never unsmoothen it.
    wv_smoothen_state.v = "enabled";
    wv_unsmoothen_state.v = "disabled";
    return;
  }
}


export function make_edges_tangent(edge_lists, fix_vertices) {
  // edge_lists contains listified lists of edges. We take all consecutive pairs and make them tangent if they aren't.

  var edge_pairs = [];
  edge_lists.forEach(function(edge_list) {
    var new_edge_pairs = find_all_pairs_in_listified_edge_list(edge_list, !edge_list_is_closed(edge_list));
    edge_pairs = edge_pairs.concat(new_edge_pairs);
  });

  // Remove any edge pairs where both edges are fixed
  for (var i = edge_pairs.length - 1; i >= 0; i--) {
    var edge_pair = edge_pairs[i];
    if (uil_is_object_fixed(edge_pair[0]) && uil_is_object_fixed(edge_pair[1]))
      edge_pairs.splice(i,1);
  }

  suppress_animate();
  suppress_undo_tracking();

  var vertices_that_we_fixed = [];
  if (fix_vertices) {
    edge_pairs.forEach(function(edge_pair){
      if (!uil_is_object_fixed(edge_pair[0]) && !uil_is_object_fixed(uil_get_vertices_of_edge(edge_pair[0])[0]))
        push_nodups(uil_get_vertices_of_edge(edge_pair[0])[0], vertices_that_we_fixed);
      if (!uil_is_object_fixed(edge_pair[0]) && !uil_is_object_fixed(uil_get_vertices_of_edge(edge_pair[0])[1]))
        push_nodups(uil_get_vertices_of_edge(edge_pair[0])[1], vertices_that_we_fixed);
      if (!uil_is_object_fixed(edge_pair[1]) && !uil_is_object_fixed(uil_get_vertices_of_edge(edge_pair[1])[0]))
        push_nodups(uil_get_vertices_of_edge(edge_pair[1])[0], vertices_that_we_fixed);
      if (!uil_is_object_fixed(edge_pair[1]) && !uil_is_object_fixed(uil_get_vertices_of_edge(edge_pair[1])[1]))
        push_nodups(uil_get_vertices_of_edge(edge_pair[1])[1], vertices_that_we_fixed);
    });
  }
  if (vertices_that_we_fixed.length > 0)
    uil_fix_objects(vertices_that_we_fixed);

  /*
  edge_pairs.forEach(function(edge_pair) {
    uil_set_joint_smoothness(edge_pair[0], edge_pair[1], 0, true, false);
  });
  */
  uil_add_joint_smoothness(edge_pairs, FAINT_STRENGTH, true, false);

  if (vertices_that_we_fixed.length > 0)
    uil_unfix_objects(vertices_that_we_fixed);

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  request_setting_button_states();
}

export function make_edges_not_tangent(edge_lists) {
    // edge_lists contains listified lists of edges. We take all consecutive pairs and make them non-tangent if they are tangent.

    var edge_pairs = [];
    edge_lists.forEach(function(edge_list) {
      var new_edge_pairs = find_all_pairs_in_listified_edge_list(edge_list, !edge_list_is_closed(edge_list));
      edge_pairs = edge_pairs.concat(new_edge_pairs);
    });

    // Remove any edge pairs where both edges are fixed
    for (var i = edge_pairs.length - 1; i >= 0; i--) {
      var edge_pair = edge_pairs[i];
      if (uil_is_object_fixed(edge_pair[0]) && uil_is_object_fixed(edge_pair[1]))
        edge_pairs.splice(i,1);
    }

    suppress_animate();
    suppress_undo_tracking();

    uil_remove_joint_smoothness(edge_pairs, false, true )

    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    request_setting_button_states();
}

export function make_selected_edges_tangent_to_selected_faces() {
  var es = uid_selected_edges.keys();
  var fs = uid_selected_faces.keys();
  suppress_animate();
  suppress_undo_tracking();
  fs.forEach(function(fid) {
    es.forEach(function(eid) {
      if (!uil_is_object_fixed(eid)) {
        // Find common vertex
        var vsf = vertices_of_face(fid);
        var vse = uil_get_vertices_of_edge(eid);
        var vid = 0;
        if (vsf.indexOf(vse[0]) != -1)
          vid = vse[0];
        if (vsf.indexOf(vse[1]) != -1)
          vid = vse[1];
        if (vid > 0) {
          var n_edges = 0;
          uil_get_edges_of_vertex(vid).forEach(function(vedgeid){
            if (uil_is_object_visible(vedgeid))
              n_edges++;
          });
          if (n_edges == 3)
            make_edge_tangent_to_face(fid, eid, vid);
          else if (n_edges > 3)
            display_message_to_user(LOG.WARN, "Currently you can only make an edge tangent to a face if no other edges outside the face boundaries are attached to their common vertex.")
        }
      }
    });
  });
  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  display_tangencies(true, true);
}

export function make_edge_tangent_to_face(fid, eid, vid) {
  // Check if the edge tangent was already fixed, and if so unfix it
  var wv_is_tan = new WrappedValue();
  var res = uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv_is_tan);
  if (wv_is_tan.v) {
    display_message_to_user(LOG.INFO, "Edge tangent was fixed, unfixing it.");
    var res = uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, false);
  }

  // If edge was linear, we remove the linearity
  if (uil_is_edge_linear(eid)) {
    uil_set_linearity_of_edges([eid], false, true);
    display_message_to_user(LOG.INFO, "Edge was constrained to be linear, removing the linearity.");
  }

  var res = uil_make_edge_tangent_to_face(eid, fid);
  // Also fix the tangency in place
  if (res)
    var res2 = uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, true);
  uid_deselect_all_objects();
  display_tangencies(true, true);
  return (res);
}

export function make_edges_perpendicular_to_plane() {
  // Add the orthogonal planes node and mirror/symmetry plane under the objects raynode so it can be ray-casted
  display_axes();

  App.state.selecting_tangent_perpendicular_plane = true;
  edge_perpendicular_plane_selected = false;
  set_button_states();
  update_dom_text_values_and_coordinates();
  animate();
}

export function complete_making_edges_perpendicular_to_plane() {
  App.state.selecting_tangent_perpendicular_plane = false;

  suppress_animate();
  suppress_undo_tracking();
  if (edge_perpendicular_plane_selected && uid_selected_edges.length > 0) {
    var edge_ids = uid_selected_edges.keys();

    // Remove linearity from any linear edges
    var linear_edge_ids = [];
    edge_ids.forEach(function(eid){
      // If edge was linear, we remove the linearity
      if (uil_is_edge_linear(eid))
        linear_edge_ids.push(eid);
    });
    if (linear_edge_ids.length > 0) {
      uil_set_linearity_of_edges(linear_edge_ids, false, true);
      display_message_to_user(LOG.INFO, "At least one edge was constrained to be linear. This linearity was removed.");
    }

    // Find the closest vertex for each edge, unless only one vertex is selected in which case use that one, and put it in a juxtaposed list
    var edge_nearest_vertex_ids = [];
    edge_ids.forEach(function(eid){
      var vs = uil_get_vertices_of_edge(eid);
      if (uid_selected_vertices.hasItem(vs[0]) && !uid_selected_vertices.hasItem(vs[1])) {
        edge_nearest_vertex_ids.push(vs[0]);
      }
      else if (uid_selected_vertices.hasItem(vs[1]) && !uid_selected_vertices.hasItem(vs[0])) {
        edge_nearest_vertex_ids.push(vs[1]);
      }
      else if (plane_point_distance(edge_perpendicular_plane_base, edge_perpendicular_plane_normal, uid_vertices.getItem(vs[0]).object3d.position) <
          plane_point_distance(edge_perpendicular_plane_base, edge_perpendicular_plane_normal, uid_vertices.getItem(vs[1]).object3d.position))
        edge_nearest_vertex_ids.push(vs[0]);
      else
        edge_nearest_vertex_ids.push(vs[1]);
    });

    // Calculate a vector opposite to the plane normal
    var n_inv = new THREE.Vector3();
    n_inv.copy(edge_perpendicular_plane_normal);
    n_inv.multiplyScalar(-1);

    // Set tangent direction of all edges at nearest tangent
    for (var i = 0; i < edge_ids.length; i++) {
      var eid = edge_ids[i];
      var vid = edge_nearest_vertex_ids[i];

      // Find the normal of the plane, and the opposite direction to the normal
      var t = uil_get_edge_tangent_at_vertex(eid, vid);
      var a = t.angleTo(edge_perpendicular_plane_normal);
      var a_inv = t.angleTo(n_inv);
      var normal = a < a_inv ? edge_perpendicular_plane_normal : n_inv;
      var eps = 0.001;

      // Remove any tangency of this edge to another edge on this vertex, unless it is already perpendicular
      var es = uil_get_edges_of_vertex(vid);
      var skip_this_edge_due_to_tangency = false;
      es.forEach(function(e1id){
        if (e1id != eid && uil_is_joint_smooth(eid, e1id)) {
          if (maths.proximate( Math.min(a, a_inv), 0.0, eps)) {
            skip_this_edge_due_to_tangency = true;
          }
          else {
            uil_remove_joint_smoothness([[eid, e1id]], false, true);
            display_message_to_user(LOG.INFO, "A tangency between a pair of edges was removed to make the edge perpendicular to the select plane.");
          }
        }
      });

      if (!skip_this_edge_due_to_tangency) {
        // Check if the edge tangent was already fixed, and if so unfix it
        var wv_is_tan = new WrappedValue();
        var res = uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv_is_tan);
        if (wv_is_tan.v) {
          display_message_to_user(LOG.INFO, "An edge tangent was already fixed, unfixing it.");
          var res = uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, false);
        }

        var res = uil_set_edge_tangent_at_vertex(eid, vid, normal);
        if (res)
          uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, true);
        else
          display_message_to_user(LOG.WARN, "Failed to make edge perpendicular to plane.");
      }
    }
  }
  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);

  uid_deselect_all_objects();
  uid_unhighlight_all_axes();
  set_button_states();
  animate();
  display_tangencies(true, true);
}

export function make_edges_tangent_parallel_to_edge() {
  // We'll make all selected edge tangent to the first selected edge.
  if (uid_selected_edges.length < 2 || uid_selected_vertices.length != 0 || uid_selected_faces.length != 0) {
    display_message_to_user(LOG.WARN, "You must select two or more edges only.");
    return
  }

  var edge_ids = [];
  edge_ids = edge_ids.concat(uid_selected_edges.keys());

  var driving_eid = edge_ids[0];
  var driving_vid = find_edge_closest_vertex_to_coordinates(driving_eid, uid_selected_edges_click_coordinates.getItem(driving_eid));

  var dir = uil_get_edge_tangent_at_vertex(driving_eid, driving_vid);
  // Calculate a vector opposite to the edge direction
  var dir_inv = new THREE.Vector3();
  dir_inv.copy(dir);
  dir_inv.multiplyScalar(-1);

  uid_deselect_all_objects();
  suppress_animate();
  suppress_undo_tracking();

  // Remove linearity from any linear edges
  var linear_edge_ids = [];
  for (var i = 1; i < edge_ids.length; i++) {
    var eid = edge_ids[i];
      // If edge was linear, we remove the linearity
    if (uil_is_edge_linear(eid))
      linear_edge_ids.push(eid);
  }
  if (linear_edge_ids.length > 0) {
    uil_set_linearity_of_edges(linear_edge_ids, false, true);
    display_message_to_user(LOG.INFO, "At least one edge was constrained to be linear. This linearity was removed.");
  }

  // Set tangent direction of all edges at nearest tangent. In the process
  // we must remove any tangencies with adjacent edges, and add them back afterwards.
  for (var i = 1; i < edge_ids.length; i++) {
    var eid = edge_ids[i];
    var vid = find_edge_closest_vertex_to_coordinates(eid, uid_selected_edges_click_coordinates.getItem(eid));

    // Check if the edge tangent was already fixed, and if so unfix it
    var wv_is_tan = new WrappedValue();
    var res = uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv_is_tan);
    if (wv_is_tan.v) {
      display_message_to_user(LOG.INFO, "An edge tangent was already fixed, unfixing it.");
      var res = uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, false);
    }

    // Check whether the edge had a tangency on it.
    var smooth_edge_id = 0;
    var es = uil_get_edges_of_vertex(vid).filter(e => e != eid).filter(e => uil_is_joint_smooth(e, eid));
    if (es.length == 1) {
      smooth_edge_id = es[0];
      uil_remove_joint_smoothness([[eid, smooth_edge_id]]);
    }

    // Check whether we want to set the tangent along the normal or along the opposite direction
    var t = uil_get_edge_tangent_at_vertex(eid, vid);
    var a = t.angleTo(dir);
    var a_inv = t.angleTo(dir_inv);
    var res = uil_set_edge_tangent_at_vertex(eid, vid, a < a_inv ? dir : dir_inv);
    if (res)
      uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, true);
    else
      display_message_to_user(LOG.WARN, "Failed to make edge tangent parallel to first edge.");

    // Add back tangency after fixing original edge
    var vs_to_unfix = [];
    if (!uil_is_object_fixed(uil_get_vertices_of_edge(eid)[0]))
      vs_to_unfix.push(uil_get_vertices_of_edge(eid)[0]);
    if (!uil_is_object_fixed(uil_get_vertices_of_edge(eid)[1]))
      vs_to_unfix.push(uil_get_vertices_of_edge(eid)[1]);
    uil_fix_objects([eid]);
    if (smooth_edge_id > 0)
      uil_add_joint_smoothness([[eid, smooth_edge_id]], global_smoothen_strength, true, false);
    uil_unfix_objects([eid]);
    uil_unfix_objects(vs_to_unfix);
    uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, true);
    if (smooth_edge_id > 0)
      uil_set_edge_fixing_of_tangent_at_vertex(smooth_edge_id, vid, true);
  }

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  display_tangencies(true, true);
  set_button_states();
  animate();
}

export function remove_tangency_of_edge_to_face() {
  var eid = uid_selected_edges.firstKey();
  var fid = uid_selected_faces.firstKey();

  // Find common vertex
  var vsf = vertices_of_face(fid);
  var vse = uil_get_vertices_of_edge(eid);
  var vid = 0;
  if (vsf.indexOf(vse[0]) != -1)
    vid = vse[0];
  else if (vsf.indexOf(vse[1]) != -1)
    vid = vse[1];
  else {
    display_message_to_user(LOG.WARN, "Edge and face must touch.");
    return 0;
  }
  // Check if the edge tangent was already fixed, and if so unfix it
  var wv_is_tan = new WrappedValue();
  var res = uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv_is_tan);
  if (wv_is_tan.v) {
    var res = uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, false);
  }
  uid_deselect_all_objects();
  display_tangencies(true, true);
  return (res);
}

export function fix_edge_tangent_at_vertex(eid = 0, vids = null) {
  unhighlight_currently_highlighted_object();
  if (!vids)
    vids = [];
  // Can handle both vertices together.
  if (eid == 0) {
    if (uid_selected_edges.length == 0)
      return 0;
    eid = uid_selected_edges.firstKey();
  }
  if (vids.length == 0 && uid_selected_vertices.length == 1)
    vids.push(uid_selected_vertices.firstKey());
  // If we have no vertices in vids, use the closest click location.
  if (vids.length == 0) {
    var vid = find_edge_closest_vertex_to_coordinates(eid, uid_selected_edge_click_point);
    if (vid)
      vids.push(vid);
  }

  // Make sure vertices belong to edge
  var all_vertices_belong_to_edge = true;
  vids.forEach(function(vid){
    if (!uil_is_vertex_of_edge(vid, eid))
      all_vertices_belong_to_edge = false;
  });

  if (!all_vertices_belong_to_edge) {
    display_message_to_user(LOG.WARN, "Vertex was not on edge, aborting.");
    //uid_deselect_all_objects();
    return (res);
  }

  // If edge was linear, we remove the linearity
  if (uil_is_edge_linear(eid)) {
    uil_set_linearity_of_edges([eid], false, true);
    //display_message_to_user(LOG.INFO, "Edge was constrained to be linear, removing the linearity.");
  }

  var res;
  vids.forEach(function(vid) {
    // Check if the edge tangent was unfixed, and if so fix it
    var wv_is_tan = new WrappedValue();
    res = uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv_is_tan);
    if (!wv_is_tan.v) {
      res = uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, true);
      // Check if this edge is smooth with another one, and if so fix the other edge tangent as well.
      var neids = get_neighbouring_edges_of_edge(eid);
      neids.forEach(function(neid){
        if (uil_find_edge_shared_vertex(eid, neid) == vid && uil_is_joint_smooth(eid, neid))
          res = uil_set_edge_fixing_of_tangent_at_vertex(neid, vid, true);
      });
    }
  });
  //uid_deselect_all_objects();

  if (App.state.tangencies_are_displayed) {
    hide_tangencies(true);
    display_tangencies(true, true);
  }

  return (res);
}

export function unfix_edge_tangent_at_vertex(eid = 0, vids = null, dont_display_tangencies = false) {
  unhighlight_currently_highlighted_object();
  if (!vids)
    vids = [];
  // Can handle both vertices together.
  if (eid == 0) {
    if (uid_selected_edges.length == 0)
      return 0;
    eid = uid_selected_edges.firstKey();
  }
  if (vids.length == 0 && uid_selected_vertices.length == 1)
    vids.push(uid_selected_vertices.firstKey());
  // If we have no vertices in vids, use the closest click location.
  if (vids.length == 0) {
    var vid = find_edge_closest_vertex_to_coordinates(eid, uid_selected_edge_click_point);
    if (vid)
      vids.push(vid);
  }

  // If we have no vertices in vids, use closest edge click location.
  if (vids.length == 0) {
    var vid = find_edge_closest_vertex_to_coordinates(eid, uid_selected_edge_click_point);
    if (vid)
      vids.push(vid);
  }

  // Make sure vertices belong to edge
  var all_vertices_belong_to_edge = true;
  vids.forEach(function(vid){
    if (!uil_is_vertex_of_edge(vid, eid))
      all_vertices_belong_to_edge = false;
  });

  if (!all_vertices_belong_to_edge) {
    display_message_to_user(LOG.WARN, "Vertex was not on edge, aborting.");
    //uid_deselect_all_objects();
    return (res);
  }

  var res;
  var tangencies_were_unfixed = false;
  vids.forEach(function(vid) {
    // Check if the edge tangent was already fixed, and if so unfix it
    var wv_is_tan = new WrappedValue();
    res = uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv_is_tan);
    if (wv_is_tan.v) {
      tangencies_were_unfixed = true;
      res = uil_set_edge_fixing_of_tangent_at_vertex(eid, vid, false);
      // Check if this edge is smooth with another one, and if so unfix the other edge tangent as well.
      var neids = get_neighbouring_edges_of_edge(eid);
      neids.forEach(function(neid){
        if (uil_find_edge_shared_vertex(eid, neid) == vid && uil_is_joint_smooth(eid, neid))
          res = uil_set_edge_fixing_of_tangent_at_vertex(neid, vid, false);
      });
    }
  });

  // Hide and show tangencies to update the state visibly.
  if (tangencies_were_unfixed && !dont_display_tangencies) {
    hide_tangencies(true);
    display_tangencies(true, true);
  }

  return (res);
}

export function check_and_report_tangencies() {
  if (!uid_edges)
    return;

  // For now just look for any fixed tangencies and display the angle
  var edge_vertex_pairs = [];
  uid_edges.keys().forEach(function(eid){
    var vs = uil_get_vertices_of_edge(eid);
    vs.forEach(function(vid){
      var wv_is_fixed = new WrappedValue();
      uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv_is_fixed);
      if (wv_is_fixed.v)
        push_nodups({"e": eid, "v": vid}, edge_vertex_pairs);
    });
  });
  var txt = "";
  edge_vertex_pairs.forEach(function(pair){
    var t = uil_get_edge_tangent_at_vertex(pair.e, pair.v);
    t.normalize();
    txt += "(" + pair.e + ", " + pair.v + "): (" + t.x.toFixed(4) + ", " + t.y.toFixed(4) + ", " + t.z.toFixed(4) + ")\n";
  });
  display_message_to_user(LOG.INFO, txt);
}

let hidden_edges_visible_before_displaying_tangency = false
export function display_tangencies(hide_later = false, dont_switch_to_hidden_edge_mode = true) {
    if (!uid_edges || App.state.tangencies_are_displayed)
        return;

    App.state.tangencies_are_displayed = true;
    if (!tangencies_are_temporarily_hidden)
        hidden_edges_visible_before_displaying_tangency = App.state.hidden_edges_visible;

    tangencies_are_temporarily_hidden = false;
    if (!dont_switch_to_hidden_edge_mode)
      App.state.hidden_edges_visible = true;

    // Add tangency display node to the scene
    scene.add(uid_tangent_display_raynode);

    // Create a hashtable indexed by eid_vid to maintain all edge tangencies
    const edges_to_display_tangency_status = new HashTable();

    // Display all fixed tangent constraints
    pipe(
        edges_for_which_to_display_tangency,
        filter(x =>
            uil_is_object_visible(x)
            && !mustNotDisplayEdge(x)
        ),
        bind(eid =>
            uil_get_vertices_of_edge(eid)
                /*.filter(vid =>
                    uil_is_object_visible(vid)
                    && !mustNotDisplayVertex(vid)
                )*/
                .map(duad(eid))
        ),
        filter(x => {
            const wv = new WrappedValue();
            uil_get_edge_fixing_of_tangent_at_vertex(...x, wv);
            return wv.v ? true : false;
        }),
        map(x => Tuple(...x)),
        set,
        each(x => add_tangency_status(...x, edges_to_display_tangency_status, 'fixed_tangent')),
    );

    // Display all edge-edge-tangency constraints

    pipe(
        edges_for_which_to_display_tangency,
        x => pairs(x, uid_edges.keys()),
        //Array.from, x => { console.log(x, '<- pairs!'); return x },
        filter(x => x[0] !== x[1] && uil_is_joint_smooth(...x)),
        map(([e1id, e2id]) => {
            const vid = uil_find_edge_shared_vertex(e1id, e2id);
            return Tuple(e1id, vid);
        }),
        set,
        each(x => add_tangency_status(...x, edges_to_display_tangency_status, 'edge_edge_tangent'))
    );

    // Display relevant vectors
    for (const eid of edges_for_which_to_display_tangency) {
        const vs = uil_get_vertices_of_edge(eid);
        for (const vid of vs)  {
            const k = eid + "_" + vid;
            if (edges_to_display_tangency_status.hasItem(k))
                display_fixed_edge_vertex_tangent(eid, vid, edges_to_display_tangency_status.getItem(k));
            else
                display_fixed_edge_vertex_tangent(eid, vid, []);
        }
    }

    animate();

    if (hide_later && !App.state.editing_edge_tangent_mode)
        setTimeout(() => hide_tangencies(), 1000);
}

export function add_tangency_status(e, v, edges_to_display_tangency_status, type) {
  var k = e + "_" + v;
  if (edges_to_display_tangency_status.hasItem(k))
    edges_to_display_tangency_status.getItem(k).push(type);
  else
    edges_to_display_tangency_status.setItem(k, [type]);
}

export function display_fixed_edge_vertex_tangent(e, v, type_array) {
  var t = uil_get_edge_tangent_at_vertex(e, v);

  // Must make sure t is pointing along the edge, and not in the opposite direction. To do this we see if t or -t has the smallest
  // angle with the vector pointing from this vertex to the opposite vertex of the edge
  var vs = uil_get_vertices_of_edge(e);
  var distant_vertex;
  if (vs[0] == v)
    distant_vertex = vs[1];
  else
    distant_vertex = vs[0];

  var vertices = uid_edges.getItem(e).object3d.geometry.vertices;
  var v0 = vertices[0];
  var v1 = vertices[1];
  var vnminus1 = vertices[vertices.length-2];
  var vn = vertices[vertices.length-1];

  if (v0.distanceTo(uid_vertices.getItem(v).object3d.position) >
      v0.distanceTo(uid_vertices.getItem(distant_vertex).object3d.position)) {
    // Need to flip sample order
    var vn = vertices[0];
    var vnminus1 = vertices[1];
    var v1 = vertices[vertices.length-2];
    var v0 = vertices[vertices.length-1];
  }

  var dv = new THREE.Vector3();
  dv.subVectors(v1, v0);
  dv.normalize();
  t.normalize();

  var t_inv = new THREE.Vector3();
  t_inv.copy(t).multiplyScalar(-1);
  var a = t.angleTo(dv);
  var a_inv = t_inv.angleTo(dv);

  add_tangent_display_geometries(e, v, uid_vertices.getItem(v).object3d.position, a < a_inv ? t : t_inv, type_array);
}

export function hide_tangencies(preserve_hidden_edges = false) {
  if (!App.state.tangencies_are_displayed)
    return;

  App.state.tangencies_are_displayed = false;
  if (!preserve_hidden_edges)
    App.state.hidden_edges_visible = hidden_edges_visible_before_displaying_tangency;
  else
    tangencies_are_temporarily_hidden = true;

  // Remove all children of the tangency display node
  for (var i = uid_tangent_display_raynode.children.length - 1; i >= 0; i--) {
    uid_tangent_display_raynode.remove(uid_tangent_display_raynode.children[i]);
  }

  // Remove tangency display node to the scene
  scene.remove(uid_tangent_display_raynode);
  animate();
}

export function add_tangent_display_geometries(eid, vid, p, d, type_array) {
  var rl, gl, bl, rp, gp, bp;
  var length;

  var type;
  if (type_array.length == 0)
    type = "free";
  else if (type_array.length == 1 && type_array[0] == "fixed_tangent")
    type = "fixed_tangent";
  else if (type_array.length == 1 && type_array[0] == "edge_edge_tangent")
    type = "edge_edge_tangent";
  else if (type_array.length == 2)
    type = "fixed_edge_edge_tangent";

  if (uil_is_object_fixed(eid)) {
    // If the edge is fixed, show the tangent fixed as well.
    rl = 0.8;
    gl = 0.4;
    bl = 0.2;
    rp = 0.8;
    gp = 0.4;
    bp = 0.2;
    length = uid_vertex_diameter * 3.3;
  }
  else if (uid_rigid_move_manipulator && uid_rigid_move_manipulator.is_for_edge_tangent &&
    uid_rigid_move_manipulator.rigid_move_edge_object_ids[0] == eid &&
    uid_rigid_move_manipulator.rigid_move_vertex_object_ids[0] == vid) {
    // We are rotating this particular vector
    rl = App.state.colours.highlighted.r/256;
    gl = App.state.colours.highlighted.g/256;
    bl = App.state.colours.highlighted.b/256;
    rp = App.state.colours.highlighted.r/256;
    gp = App.state.colours.highlighted.g/256;
    bp = App.state.colours.highlighted.b/256;
    length = uid_vertex_diameter * 3.3;
  }
  else if (uid_rigid_move_manipulator && uid_rigid_move_manipulator.is_for_edge_tangent &&
    uid_rigid_move_manipulator.rigid_move_vertex_object_ids[0] == vid &&
    uil_find_edge_shared_vertex(uid_rigid_move_manipulator.rigid_move_edge_object_ids[0], eid) == vid &&
    uil_is_joint_smooth(uid_rigid_move_manipulator.rigid_move_edge_object_ids[0], eid)) {
    // We are rotating a vector that is tangent to this particular vector
    rl = App.state.colours.highlighted.r/256;
    gl = App.state.colours.highlighted.g/256;
    bl = App.state.colours.highlighted.b/256;
    rp = App.state.colours.highlighted.r/256;
    gp = App.state.colours.highlighted.g/256;
    bp = App.state.colours.highlighted.b/256;
    length = uid_vertex_diameter * 3.3;
  }
  else if (type == "fixed_tangent" || type == "fixed_edge_edge_tangent") {
    rl = 0.8;
    gl = 0.4;
    bl = 0.2;
    rp = 0.8;
    gp = 0.4;
    bp = 0.2;
    length = uid_vertex_diameter * 3.3;
  }
  else if (type == "edge_edge_tangent") {
    rl = 0.4;
    gl = 0.2;
    bl = 0.8;
    rp = 0.4;
    gp = 0.2;
    bp = 0.8;
    length = uid_vertex_diameter * 3.3;
  }
  /*
  else if (type == "fixed_edge_edge_tangent") {
    rl = 0.4;
    gl = 0.2;
    bl = 0.8;
    rp = color_fixed_r/255;
    gp = color_fixed_g/255;
    bp = color_fixed_b/255;
    length = uid_vertex_diameter * 3.3;
  }
  */
  else if (type == "free") {
    rl = 0.2;
    gl = 0.8;
    bl = 0.4;
    rp = 0.2;
    gp = 0.8;
    bp = 0.4;
    length = uid_vertex_diameter * 3.3;
  }

  var new_point = new THREE.Vector3();
  new_point.copy(p);
  new_point.add(d.multiplyScalar(length));
  add_tangent_line_to_scene([p, new_point], rl * 255, gl * 255, bl * 255, rp * 255, gp * 255, bp * 255);
}

export function add_tangent_line_to_scene(point_vectors, rl, gl, bl, rp, gp, bp) {
  let p_material = new THREE.MeshBasicMaterial({ color: 0x777777 });
  p_material.color.setRGB(rp/256,gp/256,bp/256);
  let e_material = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 2 });
  e_material.color.setRGB(rl/256,gl/256,bl/256);

  for (let i = 0; i < point_vectors.length; i++) {
    var p_geometry = new THREE.SphereGeometry(uid_vertex_diameter / 4.0, 8, 6);
    p_geometry.dynamic = true;
    var p_mesh = new THREE.Mesh(p_geometry, p_material);
    // Position the object in 3D space
    p_mesh.position.set(point_vectors[i].x, point_vectors[i].y, point_vectors[i].z);
    uid_tangent_display_raynode.add(p_mesh);
    if (i > 0) {
      var e_geometry = new THREE.Geometry();
      e_geometry.dynamic = true;
      e_geometry.vertices = new Array();
      e_geometry.vertices.push(point_vectors[i-1]);
      e_geometry.vertices.push(point_vectors[i]);
      var e_mesh = new THREE.Line(e_geometry, e_material, THREE.LineStrip);
      uid_tangent_display_raynode.add(e_mesh);
    }
  }
}
